import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Linkedin } from "lucide-react";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { trackLandingCta } from "@/lib/track-landing-cta";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

// Landing-only palette (mirrors src/routes/index.tsx)
const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const GOLD_2 = "#D9B761";
const INK = "#14213A";
const INK_2 = "#545C6B";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const PRIMARY_CTA = { to: "/auth", search: { mode: "signup" as const } } as const;

const ABOUT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  mainEntity: {
    "@type": "Organization",
    name: "CapitalDart",
    url: "https://capitaldart.com",
    founder: [
      {
        "@type": "Person",
        name: "Ken Margolis",
        jobTitle: "Co-Founder",
        sameAs: "https://www.linkedin.com/in/kennymargolis/",
        alumniOf: ["Merrill Lynch", "Credit Suisse First Boston"],
        image: "https://capitaldart.com/ken-margolis.jpg",
      },
      {
        "@type": "Person",
        name: "Richard Luftig",
        jobTitle: "Co-Founder",
        sameAs: "https://www.linkedin.com/in/richard-luftig-51a94b4",
        alumniOf: [
          "Bear, Stearns & Co.",
          "Credit Suisse First Boston",
          "University of Pennsylvania",
          "New York University School of Law",
        ],
        image: "https://capitaldart.com/richard-luftig.jpg",
      },
    ],
  },
};

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About CapitalDart" },
      {
        name: "description",
        content:
          "Why two senior investment bankers with more than fifty years between them at Credit Suisse First Boston, Merrill Lynch, and Bear Stearns built CapitalDart.",
      },
      { property: "og:title", content: "About CapitalDart" },
      {
        property: "og:description",
        content:
          "Why two senior investment bankers with more than fifty years between them at Credit Suisse First Boston, Merrill Lynch, and Bear Stearns built CapitalDart.",
      },
      { property: "og:url", content: "https://capitaldart.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "About CapitalDart" },
      {
        name: "twitter:description",
        content:
          "Why two senior investment bankers with more than fifty years between them at Credit Suisse First Boston, Merrill Lynch, and Bear Stearns built CapitalDart.",
      },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(ABOUT_JSON_LD),
      },
    ],
  }),
  component: AboutPage,
});

function FounderProfile({
  name,
  image,
  linkedin,
  bio,
}: {
  name: string;
  image: string;
  linkedin: string;
  bio: string;
}) {
  return (
    <section
      style={{
        display: "flex",
        gap: 36,
        alignItems: "flex-start",
        padding: "48px 0",
        borderTop: `1px solid ${LINE}`,
      }}
      className="max-md:flex-col max-md:gap-6"
    >
      <img
        src={image}
        alt={name}
        width={220}
        height={220}
        loading="lazy"
        className="max-md:w-full max-md:max-w-[280px]"
        style={{
          width: 220,
          height: 220,
          objectFit: "cover",
          borderRadius: 4,
          border: `1px solid ${LINE}`,
          flexShrink: 0,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 600,
              fontSize: 26,
              lineHeight: 1.2,
              letterSpacing: "-.01em",
              color: INK,
              margin: 0,
            }}
          >
            {name}, Co-Founder
          </h2>
          <a
            href={linkedin}
            target="_blank"
            rel="noopener"
            aria-label={`${name} on LinkedIn`}
            style={{ color: INK_2, display: "inline-flex" }}
          >
            <Linkedin size={20} strokeWidth={1.75} />
          </a>
        </div>
        <p
          style={{
            fontFamily: FONT_SANS,
            fontSize: 16,
            lineHeight: 1.7,
            color: INK_2,
            marginTop: 14,
            marginBottom: 0,
          }}
        >
          {bio}
        </p>
      </div>
    </section>
  );
}

function AboutPage() {
  return (
    <main
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased"
    >
      <AboutNav />

      <section
        style={{ background: PAPER_2, padding: "88px 0 56px", borderBottom: `1px solid ${LINE}` }}
      >
        <div className="mx-auto" style={{ maxWidth: 980, padding: "0 32px" }}>
          <div
            style={{
              fontFamily: FONT_MONO,
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 18,
            }}
          >
            About CapitalDart
          </div>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(36px,4.2vw,56px)",
              lineHeight: 1.05,
              letterSpacing: "-.01em",
              margin: 0,
            }}
          >
            Why we built CapitalDart
          </h1>
        </div>
      </section>

      <section style={{ padding: "64px 0 96px" }}>
        <div className="mx-auto" style={{ maxWidth: 980, padding: "0 32px" }}>
          <div
            style={{
              fontFamily: FONT_SANS,
              fontSize: 16.5,
              lineHeight: 1.75,
              color: INK_2,
            }}
          >
            <p>
              We have spent our careers raising capital. Between us that is more than fifty years
              at Credit Suisse First Boston, Merrill Lynch, and Bear Stearns, and since 2009 at the
              investment bank we founded together, where we and about forty senior bankers have
              taken hundreds of companies, funds, and real estate sponsors to institutional
              investors.
            </p>
            <p>
              Over those years we learned what actually moves a raise. It is not a bigger list. It
              is knowing which investor contact at which firm invests in your kind of deal right
              now, why they would take the meeting, what to say in the first email, and what to do
              when they go quiet. That knowledge sits inside a small number of investment banks.
              Most companies raising under $50 million cannot get a bank's attention at all.
            </p>
            <p>
              The alternative has been an investor database, some better than others but usually
              you pay for what you get: thousands of firm names, a generic contact, and no view of
              who at the firm fits or why. Every CEO we have watched try that approach has spent
              months on the wrong names.
            </p>
            <p>
              CapitalDart is the process we run, built as software. It reads your materials, finds
              the specific person at each firm whose criteria match your deal, tells you why,
              drafts the outreach in your voice, reviews your deck and model the way an investor
              will, strategizes, and tracks every conversation through close for your entire team.
              It costs $250 (after rebate) for 500 investors, once. There is no subscription and no
              placement fee. Whether you are raising capital on your own or want to supplement what
              your capital raiser is doing, we built CapitalDart to give you the highest quality
              contacts and platform at an exceptionally low price to raise your capital.
            </p>
            <p style={{ marginBottom: 0 }}>
              <span
                style={{
                  display: "block",
                  fontFamily: FONT_SERIF,
                  fontSize: 19,
                  color: INK,
                }}
              >
                Ken Margolis and Richard Luftig
              </span>
              <span style={{ display: "block", color: INK_2 }}>Co-Founders</span>
            </p>
          </div>

          <div style={{ marginTop: 64 }}>
            <FounderProfile
              name="Ken Margolis"
              image="/ken-margolis.jpg"
              linkedin="https://www.linkedin.com/in/kennymargolis/"
              bio="Ken has spent more than 25 years in investment banking and raising capital from institutional private equity and venture investors, family offices, foundations, endowments, hedge funds, banks, and specialty finance lenders. In 2009 he co-founded a FINRA-member investment bank that raises private equity and debt capital for early-stage and middle-market companies across financial services and fintech, real estate, technology, business services, energy and real assets, and consumer and retail. From 1999 to 2007 he was at Merrill Lynch as co-head of the 50-person global structured credit products investment banking, sales, and syndicate group, and head of the U.S. alternative investment group raising capital for alternative investment funds. From 1991 to 1999 he was at Credit Suisse First Boston as a senior trader on a principal transaction desk and head of the firm's new issue whole loan and agency CMO desk. He began his career in 1985 at Arthur Andersen, managing the structured finance group's work on early securitizations of residential mortgages, credit cards, auto loans, and corporate loans. FINRA Series 7, 24, 63, and 79."
            />
            <FounderProfile
              name="Richard Luftig"
              image="/richard-luftig.jpg"
              linkedin="https://www.linkedin.com/in/richard-luftig-51a94b4"
              bio="Richard has more than 25 years in investment banking, capital markets, real estate, proprietary investing, and alternative investments. In 2009 he co-founded the investment bank with Ken. He was a Managing Director at Ivy Asset Management, responsible for investment strategy, risk management, and portfolio construction, and a member of the firm's Investment Risk Management Committee. At Bear, Stearns & Co. he was Managing Director and Principal, head of North American Structured Credit Distribution, structuring and distributing structured finance products and executing debt capital markets transactions for large financial institutions. He was a Director in the Principal Transactions Group at Credit Suisse First Boston, investing the firm's capital in debt secured by a range of asset classes and commercial mortgage-backed securities. B.S. in Economics from the Wharton School of the University of Pennsylvania, and a J.D. and LL.M. in Taxation from New York University School of Law. FINRA Series 7, 24, 63, and 79."
            />
          </div>

          <section
            style={{
              marginTop: 48,
              paddingTop: 48,
              borderTop: `1px solid ${LINE}`,
            }}
          >
            <h2
              style={{
                fontFamily: FONT_SERIF,
                fontWeight: 600,
                fontSize: 26,
                lineHeight: 1.2,
                letterSpacing: "-.01em",
                color: INK,
                margin: 0,
              }}
            >
              What CapitalDart is
            </h2>
            <p
              style={{
                fontFamily: FONT_SANS,
                fontSize: 16.5,
                lineHeight: 1.75,
                color: INK_2,
                marginTop: 14,
                marginBottom: 0,
              }}
            >
              CapitalDart is a software platform. It is not an investment firm, fund, placement
              agent, or broker-dealer. It does not raise capital, hold client funds, or take a
              placement fee.
            </p>
          </section>

          <div style={{ marginTop: 56 }}>
            <Link
              to={PRIMARY_CTA.to}
              search={PRIMARY_CTA.search}
              onClick={() => trackLandingCta("about_page", "Free Trial. No Credit Card. →")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: FONT_SANS,
                fontWeight: 600,
                color: NAVY,
                background: GOLD,
                borderRadius: 2,
                letterSpacing: ".01em",
                textDecoration: "none",
                transition: "background .15s",
                fontSize: 15,
                padding: "13px 26px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = GOLD_2;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = GOLD;
              }}
            >
              Free Trial. No Credit Card. →
            </Link>
          </div>
        </div>
      </section>

      <AboutFooter />
    </main>
  );
}

function AboutNav() {
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const isAuthed = Boolean(session);
  return (
    <header
      style={{
        background: NAVY,
        borderBottom: `1px solid rgba(255,255,255,0.06)`,
        position: "relative",
      }}
    >
      <style>{`
        @media (max-width: 1023.98px) {
          .cd-aboutnav-desktop { display: none !important; }
          .cd-aboutnav-mobile { display: inline-flex !important; }
        }
      `}</style>
      <div
        className="mx-auto"
        style={{
          maxWidth: 1200,
          padding: "18px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Logo
            src={LOGO_LIGHT}
            alt="CapitalDart"
            height={34}
            style={{ display: "block" }}
            trademarkClassName="text-[#F4F1E8]"
          />
        </Link>
        <nav
          className="cd-aboutnav-desktop"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          {loading ? null : isAuthed ? (
            <>
              <Link
                to="/dashboard"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate({ to: "/" });
                }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/"
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  color: NAVY_TEXT_DIM,
                  textDecoration: "none",
                }}
              >
                Back to home
              </Link>
              <Link
                to="/auth"
                search={{ mode: "signup" as const }}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  background: GOLD,
                  color: NAVY_DEEP,
                  padding: "10px 18px",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                Get started
              </Link>
            </>
          )}
        </nav>
        <MobileNav className="cd-aboutnav-mobile" />
      </div>
    </header>
  );
}

function AboutFooter() {
  return (
    <footer
      style={{ background: NAVY_DEEP, color: NAVY_TEXT, padding: "48px 0", textAlign: "center" }}
    >
      <div className="mx-auto" style={{ maxWidth: 1200, padding: "0 32px" }}>
        <Logo
          src={LOGO_LIGHT}
          alt="CapitalDart"
          height={26}
          style={{ display: "inline-block", marginBottom: 14 }}
          trademarkClassName="text-[#F4F1E8]"
        />
        <div style={{ fontFamily: FONT_SANS, fontSize: 13, color: "#7E8A9C" }}>
          © {new Date().getFullYear()} CapitalDart. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
